"use client";
import {
  SourcePagesEnum,
  sourcePagesValues,
  TV_SIDE_NAVIGATION,
} from "@/constants/constants";
import { useClientTranslations } from "@/hooks/useClientTranslations";
import { useDialect } from "@/hooks/useDialect";
import {
  FocusContext,
  navigateByDirection,
  setFocus,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useMemo } from "react";

import { routes } from "@/constants/navigationRoutes";
import { useUserContext } from "@/context/UserProvider";
import {
  clearCloseModalOnWindow,
  setCloseModalOnWindow,
  setIsModalOnWindow,
} from "@/utils/common.helpers";
import { windowHelpers } from "@/utils/window.helpers";
import {
  CategoriesActiveIcon,
  CategoriesInactiveIcon,
  HomeFilled,
  HomeNoFill,
  MoviesActiveIcon,
  MoviesInactiveIcon,
  MyListFilled,
  MyListNoFill,
  SearchFilled,
  SearchNoFill,
  SettingsFilled,
  SettingsNoFill,
  ShowsActiveIcon,
  ShowsInactiveIcon,
} from "./Icons";
import { Img } from "./Img";
import { Column, Row } from "./Stack";
import { Text } from "./Text";

interface NavItem {
  name: string;
  selectedIcon: React.ComponentType<{
    height?: number;
    width?: number;
    color?: string;
  }>;
  unSelectedIcon: React.ComponentType<{
    height?: number;
    width?: number;
    color?: string;
  }>;
  path: string;
}

/**
 * SPATIAL NAVIGATION LAYOUT WORKAROUNDS
 *
 * The norigin spatial navigation library has issues with overlapping elements,
 * so we implement several layout hacks to ensure proper focus behavior:
 *
 * 1. Container Width Constraint: We hardcode the focus provider's width to prevent
 *    overlapping with sibling elements in the layout
 *
 * 2. Navigation Expansion: The inner nav element gets full screen width when expanded
 *    to match the design while maintaining proper focus boundaries
 *
 * 3. Focus Target Isolation: We attach the focus ref to individual icons instead of
 *    the entire navigation item to prevent focus conflicts with adjacent elements
 *
 * 4. Visual Styling Override: We style the entire navigation item for visual consistency
 *    while keeping focus behavior isolated to the icon element
 */

/**
 * Helper function to close the side navigation by navigating focus to the right
 * This moves focus away from the navigation, causing it to collapse
 */
const closeSideNavigation = () => {
  navigateByDirection("right", {});
};

export const TvSideNavigation = () => {
  const { t, currentLanguage } = useClientTranslations();
  const { currentDialect } = useDialect();

  const pathname = usePathname();

  const basePath = `/${currentLanguage}/${currentDialect.dialect.toLowerCase()}`;

  const routes = [
    {
      name: t("common.search"),
      unSelectedIcon: SearchNoFill,
      selectedIcon: SearchFilled,
      path: `${basePath}/search`,
    },
    {
      name: t("common.home"),
      unSelectedIcon: HomeNoFill,
      selectedIcon: HomeFilled,
      path: `${basePath}`,
    },
    {
      name: t("common.movies"),
      unSelectedIcon: MoviesInactiveIcon,
      selectedIcon: MoviesActiveIcon,
      path: `${basePath}/movies`,
    },

    {
      name: t("common.shows"),
      unSelectedIcon: ShowsInactiveIcon,
      selectedIcon: ShowsActiveIcon,
      path: `${basePath}/shows`,
    },
    {
      name: t("common.categories"),
      unSelectedIcon: CategoriesInactiveIcon,
      selectedIcon: CategoriesActiveIcon,
      path: `${basePath}/genre`,
    },
    {
      name: t("common.myList"),
      unSelectedIcon: MyListNoFill,
      selectedIcon: MyListFilled,
      path: `${basePath}/my-list`,
    },
    {
      name: t("setting_page.title"),
      unSelectedIcon: SettingsNoFill,
      selectedIcon: SettingsFilled,
      path: `${basePath}/settings`,
    },
  ];

  const currentRoute = useMemo(() => {
    return routes.findIndex((route) => route.path === pathname);
  }, [pathname]);

  // On genre page, we don't want the overlay expansion behavior
  // so that the CategorySidebar remains visible
  const isGenrePage = pathname.includes("/genre");

  const {
    ref: focusRef,
    focusKey,
    hasFocusedChild,
  } = useFocusable({
    focusBoundaryDirections: ["up", "down", "left"],
    isFocusBoundary: true,
    trackChildren: true,
    focusKey: "SIDE_NAVIGATION",
    saveLastFocusedChild: false,
    // Set initial focus to the navigation item corresponding to the current route
    preferredChildFocusKey: `SIDE_NAVIGATION_ITEM_${currentRoute}`,
  });
  // Show expanded labels only when navigation is focused
  // On genre page: no overlay (so CategorySidebar remains visible)
  // On other pages: show overlay when focused
  const shouldShowExpandedView = hasFocusedChild;
  const shouldShowOverlay = hasFocusedChild && !isGenrePage;

  // On genre page when focused, use expanded width without overlay
  const navWidth =
    isGenrePage && hasFocusedChild
      ? TV_SIDE_NAVIGATION.EXPANDED_WIDTH
      : TV_SIDE_NAVIGATION.MIN_WIDTH;

  useEffect(() => {
    if (shouldShowOverlay) {
      setCloseModalOnWindow(() => {
        closeSideNavigation();
      });
      setIsModalOnWindow(true);
    } else {
      setIsModalOnWindow(false);
      clearCloseModalOnWindow();
    }
  }, [shouldShowOverlay]);

  // Set CSS variable for nav width so content can adjust dynamically
  useEffect(() => {
    if (isGenrePage) {
      document.documentElement.style.setProperty("--tv-nav-width", navWidth);
    }
    return () => {
      document.documentElement.style.removeProperty("--tv-nav-width");
    };
  }, [navWidth, isGenrePage]);

  return (
    <div
      className={`fixed left-0 top-0 z-[51] flex h-full flex-col justify-center border-0 bg-black`}
      style={{
        minWidth: TV_SIDE_NAVIGATION.MIN_WIDTH,
        width: navWidth,
      }}
      data-testid="tv-side-navigation"
    >
      <FocusContext.Provider value={focusKey}>
        <div
          ref={focusRef}
          className="relative h-full"
          style={{
            /* HACK 1: Constrain container width to prevent navigation sidebar from overlapping with the main content (See component initialization comments for detailed context) */
            maxWidth: shouldShowOverlay
              ? TV_SIDE_NAVIGATION.MIN_WIDTH
              : navWidth,
          }}
        >
          <ProfileHeader
            isExpanded={shouldShowExpandedView}
            isGenrePage={isGenrePage}
          />
          {/* HACK 2: Expand navigation to full screen width when focused (See component initialization comments for detailed context) */}
          <nav
            className={`grid h-full grid-flow-row content-center gap-5 overflow-auto ${shouldShowOverlay ? "w-screen justify-start bg-tv-side-navigation-gradient" : shouldShowExpandedView ? "w-auto justify-start" : "w-full justify-center"}`}
          >
            {routes.map((item, index) => (
              <TvNavItem
                key={item.name}
                item={item}
                isActive={pathname === item.path}
                shouldShowExpandedView={shouldShowExpandedView}
                isGenrePage={isGenrePage}
                index={index}
              />
            ))}
          </nav>
        </div>
      </FocusContext.Provider>
    </div>
  );
};

interface TvNavItemProps {
  item: NavItem;
  isActive: boolean;
  shouldShowExpandedView: boolean;
  isGenrePage: boolean;
  index: number;
}

const TvNavItem = ({
  item,
  isActive,
  shouldShowExpandedView,
  isGenrePage,
  index,
}: TvNavItemProps) => {
  const router = useRouter();
  const { ref: navItemRef, focused } = useFocusable({
    focusKey: `SIDE_NAVIGATION_ITEM_${index}`,
    onEnterPress: () => {
      // Automatically close expanded view by navigating focus away
      navigateByDirection("right", {});
      router.push(item.path);
    },
    onArrowPress: (direction) => {
      if (direction === "right" && isGenrePage) {
        // Focus the first category sidebar item when navigating right from the side navigation on genre listing pages
        // (Prevents default behaviour where focus would jump to the first content thumbnail instead.)
        setFocus("category-item-0");
        return false;
      }
      return true;
    },
  });
  const SelectedIcon = item.selectedIcon;
  const UnselectedIcon = item.unSelectedIcon;

  // Dynamic color scheme: black when focused, white when active, gray as default
  const color = focused ? "#000" : isActive ? "#fff" : "#B6B6B6";

  return (
    <Link
      href={item.path}
      // HACK 4: Style the entire item while keeping focus isolated to the icon (See component initialization comments for detailed context)
      className={`grid grid-flow-col gap-3 rounded-lg py-3 ${shouldShowExpandedView ? "mx-4 pl-3 pr-14" : ""} items-center justify-start
        ${focused ? "bg-white text-black" : ""}
        focus:outline-none
      `}
    >
      {/* HACK 3: Attach focus ref to icon only to prevent overlap conflicts (See component initialization comments for detailed context) */}
      <div ref={navItemRef}>
        {isActive ? (
          <SelectedIcon height={32} width={32} color={color} />
        ) : (
          <UnselectedIcon height={32} width={32} color={color} />
        )}
      </div>
      {shouldShowExpandedView && (
        <p
          className={`text-2xl ${isActive ? "font-bold" : "font-medium"}`}
          style={{
            color: color,
          }}
        >
          {item.name}
        </p>
      )}
    </Link>
  );
};

const ProfileHeader = ({
  isExpanded,
  isGenrePage,
}: {
  isExpanded: boolean;
  isGenrePage: boolean;
}) => {
  const { selectedProfile } = useUserContext();
  const { t, currentLanguage } = useClientTranslations();
  const router = useRouter();

  const profileSelectionPath = `/${currentLanguage}/${routes.profile.select}`;

  const { ref: profileHeaderRef, focused } = useFocusable({
    onEnterPress: () => {
      windowHelpers.setSourceOfProfileSelection(
        sourcePagesValues[SourcePagesEnum.HOME],
      );
      closeSideNavigation();
      router.push(profileSelectionPath);
      return false;
    },
    onArrowPress: (direction) => {
      if (direction === "right" && isGenrePage) {
        // Focus the first category sidebar item when navigating right from the side navigation on genre listing pages
        // (Prevents default behaviour where focus would jump to the first content thumbnail instead.)
        setFocus("category-item-0");
        return false;
      }
      return true;
    },
  });
  useEffect(() => {
    router.prefetch(profileSelectionPath);
  }, []);
  if (!selectedProfile) {
    return null;
  }
  return (
    <Link
      href={profileSelectionPath}
      className={`absolute top-[10vh] flex flex-col items-center justify-center gap-3 rounded-lg py-3 ${focused ? "bg-white text-black" : ""} ${isExpanded ? "mx-4 px-3" : "left-1/2 -translate-x-1/2"}`}
    >
      <Row fullWidth align="center" wrap={false} useGrid gap={2}>
        <div className="relative h-12	w-12">
          <Img
            src={`${process.env.NEXT_PUBLIC_CDN_ROOT_URL}${selectedProfile.avatar}`}
            alt={selectedProfile.displayName}
            className="rounded-lg object-cover"
            ref={profileHeaderRef}
            fill={true}
          />
        </div>

        {isExpanded && (
          <Column fullWidth>
            <div className="min-w-[16ch]">
              <Text appearance={focused ? "black" : "primary"}>
                <span className="text-2xl font-bold">
                  {selectedProfile.displayName}
                </span>
              </Text>
              {isExpanded && (
                <Text appearance="secondary">
                  <span
                    className={` text-xl ${focused ? "text-stg-grey-3" : ""}`}
                  >
                    {t("profile_assets.tv.change_profile_text")}
                  </span>
                </Text>
              )}
            </div>
          </Column>
        )}
      </Row>
    </Link>
  );
};
